import { Link } from 'react-router-dom';
import {
  Table, Button, Form, Badge, Card, Row, Col, InputGroup,
  Stack,
} from 'react-bootstrap';
import {
  FaTimes, FaTrash, FaEdit, FaCheck, FaSearch, FaUsers,
  FaUserShield, FaLayerGroup, FaCalendarAlt,
} from 'react-icons/fa';
import Message from '../../Components/Message';
import Loader from '../../Components/Loader';
import { toast } from 'react-toastify';
import { useState, useMemo } from 'react';
import { useGetUsersQuery, useDeleteUserMutation, useUpdateUserMutation } from '../../slices/usersApiSlice';
import {
  useAdminAssignUserTierMutation,
  useAdminListPricingTiersQuery,
  useAdminRemoveUserTierMutation,
} from '../../slices/partnerCodesApiSlice';
import BackButton from '../../Components/BackButton';

/* ── helpers ──────────────────────────────────────────────── */
const getInitials = (name = '') =>
  name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('');

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const AVATAR_PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f97316',
  '#eab308', '#22c55e', '#06b6d4', '#3b82f6',
];
const avatarColor = (id = '') => AVATAR_PALETTE[id.charCodeAt(id.length - 1) % AVATAR_PALETTE.length];

const FILTERS = [
  { key: 'all',    label: 'All' },
  { key: 'admins', label: 'Admins' },
  { key: 'tiered', label: 'Tiered' },
  { key: 'eom',    label: 'EOM Billing' },
  { key: 'freeDelivery', label: 'Free Delivery' },
  { key: 'notier', label: 'No Tier' },
];

/* ── component ────────────────────────────────────────────── */
const UserListScreen = () => {
  const { data: users = [], refetch, isLoading, error } = useGetUsersQuery();
  const [deleteUser,   { isLoading: loadingDelete }]   = useDeleteUserMutation();
  const [updateUser,   { isLoading: loadingUpdate }]   = useUpdateUserMutation();
  const [assignTier,   { isLoading: loadingAssign }]   = useAdminAssignUserTierMutation();
  const [removeTier,   { isLoading: loadingRemove }]   = useAdminRemoveUserTierMutation();
  const { data: tiers = [], isLoading: loadingTiers }  = useAdminListPricingTiersQuery();

  const [search,       setSearch]       = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [tierDraftByUser, setTierDraftByUser] = useState({});

  /* ── draft helpers ── */
  const setTierDraft = (userId, value) =>
    setTierDraftByUser((prev) => ({ ...prev, [userId]: value }));

  const getTierDraft = (user) => {
    const draft = tierDraftByUser[user._id];
    return draft !== undefined ? draft : (user?.tier?.id || '');
  };

  /* ── filtering ── */
  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const matchSearch =
        !q ||
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u._id?.toLowerCase().includes(q);

      const matchFilter =
        activeFilter === 'all'    ? true :
        activeFilter === 'admins' ? u.isAdmin :
        activeFilter === 'tiered' ? Boolean(u.tier?.id) :
        activeFilter === 'eom'    ? u.endOfMonthPaymentEnabled :
        activeFilter === 'freeDelivery' ? Boolean(u.freeDelivery) :
        activeFilter === 'notier' ? !u.tier?.id : true;

      return matchSearch && matchFilter;
    });
  }, [users, search, activeFilter]);

  /* ── stats ── */
  const stats = useMemo(() => ({
    total:  users.length,
    admins: users.filter((u) => u.isAdmin).length,
    tiered: users.filter((u) => u.tier?.id).length,
    eom:    users.filter((u) => u.endOfMonthPaymentEnabled).length,
    freeDelivery: users.filter((u) => u.freeDelivery).length,
  }), [users]);

  /* ── handlers ── */
  const assignTierHandler = async (user) => {
    const tierId = getTierDraft(user);
    if (!tierId) { toast.error('Select a tier first.'); return; }
    try {
      await assignTier({ userId: user._id, tierId }).unwrap();
      toast.success(`Tier updated for ${user.name}.`);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Tier update failed.');
    }
  };

  const removeTierHandler = async (user) => {
    if (!window.confirm(`Remove tier from ${user.name}?`)) return;
    try {
      await removeTier({ userId: user._id }).unwrap();
      toast.success(`Tier removed from ${user.name}.`);
      setTierDraft(user._id, '');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Tier removal failed.');
    }
  };

  const toggleField = async (user, field) => {
    try {
      await updateUser({
        userId: user._id,
        name:  user.name,
        email: user.email,
        isAdmin: field === 'isAdmin' ? !user.isAdmin : user.isAdmin,
        endOfMonthPaymentEnabled:
          field === 'endOfMonthPaymentEnabled'
            ? !user.endOfMonthPaymentEnabled
            : user.endOfMonthPaymentEnabled,
        freeDelivery:
          field === 'freeDelivery'
            ? !user.freeDelivery
            : Boolean(user.freeDelivery),
      }).unwrap();
      toast.success('User updated.');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Update failed.');
    }
  };

  const deleteHandler = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await deleteUser(id).unwrap();
      toast.success('User deleted.');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || err?.error || 'Delete failed.');
    }
  };

  /* ── render ── */
  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || error?.error || 'Failed to load users.'}</Message>;

  return (
    <>
      <BackButton />
      <h1 className="mb-4">Users</h1>

      {/* ── Stats row ── */}
      <Row className="g-3 mb-4">
        {[
          { label: 'Total Users',  value: stats.total,  icon: <FaUsers />,       color: '#6366f1' },
          { label: 'Admins',       value: stats.admins, icon: <FaUserShield />,   color: '#ec4899' },
          { label: 'Tiered',       value: stats.tiered, icon: <FaLayerGroup />,   color: '#22c55e' },
          { label: 'EOM Billing',  value: stats.eom,    icon: <FaCalendarAlt />,  color: '#f97316' },
          { label: 'Free Delivery', value: stats.freeDelivery, icon: <FaCalendarAlt />, color: '#06b6d4' },
        ].map(({ label, value, icon, color }) => (
          <Col key={label} xs={6} md={3} lg>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 44, height: 44, borderRadius: 10,
                    background: `${color}22`,
                    color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, flexShrink: 0,
                  }}
                >
                  {icon}
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{value}</div>
                  <div className="text-muted" style={{ fontSize: 12 }}>{label}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Search + filters ── */}
      <div className="mb-3 d-flex flex-column flex-sm-row gap-2 align-items-sm-center">
        <InputGroup style={{ maxWidth: 320 }}>
          <InputGroup.Text><FaSearch /></InputGroup.Text>
          <Form.Control
            placeholder="Search name, email, ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Stack direction="horizontal" gap={1} className="flex-wrap">
          {FILTERS.map(({ key, label }) => (
            <Button
              key={key}
              size="sm"
              variant={activeFilter === key ? 'primary' : 'outline-secondary'}
              onClick={() => setActiveFilter(key)}
            >
              {label}
            </Button>
          ))}
        </Stack>
      </div>

      {/* ── Table ── */}
      {(loadingDelete || loadingUpdate || loadingAssign || loadingRemove) && <Loader />}

      {visibleUsers.length === 0 ? (
        <Message>No users match your search.</Message>
      ) : (
        <Table striped bordered hover responsive className="table-sm align-middle">
          <thead>
            <tr>
              <th>User</th>
              <th>Joined</th>
              <th style={{ minWidth: 260 }}>Tier</th>
              <th className="text-center align-middle" style={{ width: 90 }}>Admin</th>
              <th className="text-center align-middle" style={{ width: 110 }}>EOM Pay</th>
              <th className="text-center align-middle" style={{ width: 130 }}>Free Delivery</th>
              <th className="text-center align-middle" style={{ width: 110 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map((user) => {
              const hasTier = Boolean(user.tier?.id);
              const tierDraft = getTierDraft(user);

              return (
                <tr key={user._id}>
                  {/* Avatar + name/email */}
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <div
                        style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: avatarColor(user._id),
                          color: '#fff', fontWeight: 700, fontSize: 13,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(user.name)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, lineHeight: 1.2 }}>{user.name}</div>
                        <a href={`mailto:${user.email}`} style={{ fontSize: 12 }} className="text-muted">
                          {user.email}
                        </a>
                      </div>
                    </div>
                  </td>

                  {/* Joined */}
                  <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                    {fmtDate(user.createdAt)}
                  </td>

                  {/* Tier */}
                  <td>
                    {loadingTiers ? (
                      <small className="text-muted">Loading…</small>
                    ) : (
                      <div className="d-flex flex-column gap-1">
                        {hasTier && (
                          <div className="d-flex align-items-center gap-1">
                            <Badge bg="success" style={{ fontSize: 11 }}>{user.tier.name}</Badge>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-danger"
                              style={{ fontSize: 11 }}
                              onClick={() => removeTierHandler(user)}
                              disabled={loadingRemove}
                              title="Remove tier"
                            >
                              <FaTimes />
                            </Button>
                          </div>
                        )}
                        <div className="d-flex gap-1">
                          <Form.Select
                            size="sm"
                            value={tierDraft}
                            onChange={(e) => setTierDraft(user._id, e.target.value)}
                            style={{ fontSize: 12 }}
                          >
                            <option value="">— set tier —</option>
                            {tiers.map((t) => (
                              <option key={t._id} value={t._id}>{t.name}</option>
                            ))}
                          </Form.Select>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => assignTierHandler(user)}
                            disabled={loadingAssign || !tierDraft}
                            style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                          >
                            Set
                          </Button>
                        </div>
                      </div>
                    )}
                  </td>

                  {/* Admin toggle */}
                  <td className="text-center align-middle" style={{ width: 90 }}>
                    <div className="d-flex justify-content-center align-items-center">
                      <Form.Check type="switch" className="m-0 p-0 d-flex justify-content-center">
                        <Form.Check.Input
                          type="checkbox"
                          className="m-0"
                          checked={Boolean(user.isAdmin)}
                          onChange={() => toggleField(user, 'isAdmin')}
                          disabled={loadingUpdate}
                          title={user.isAdmin ? 'Revoke admin' : 'Grant admin'}
                        />
                      </Form.Check>
                    </div>
                  </td>

                  {/* EOM toggle */}
                  <td className="text-center align-middle" style={{ width: 110 }}>
                    <div className="d-flex justify-content-center align-items-center">
                      <Form.Check type="switch" className="m-0 p-0 d-flex justify-content-center">
                        <Form.Check.Input
                          type="checkbox"
                          className="m-0"
                          checked={Boolean(user.endOfMonthPaymentEnabled)}
                          onChange={() => toggleField(user, 'endOfMonthPaymentEnabled')}
                          disabled={loadingUpdate}
                          title={user.endOfMonthPaymentEnabled ? 'Disable EOM' : 'Enable EOM'}
                        />
                      </Form.Check>
                    </div>
                  </td>

                  {/* Free Delivery toggle */}
                  <td className="text-center align-middle" style={{ width: 130 }}>
                    <div className="d-flex justify-content-center align-items-center">
                      <Form.Check type="switch" className="m-0 p-0 d-flex justify-content-center">
                        <Form.Check.Input
                          type="checkbox"
                          className="m-0"
                          checked={Boolean(user.freeDelivery)}
                          onChange={() => toggleField(user, 'freeDelivery')}
                          disabled={loadingUpdate}
                          title={user.freeDelivery ? 'Disable Free Delivery' : 'Enable Free Delivery'}
                        />
                      </Form.Check>
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="text-center align-middle" style={{ width: 110 }}>
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <Link to={`/admin/user/${user._id}/edit`}>
                        <Button variant="light" size="sm" title="Edit user">
                          <FaEdit />
                        </Button>
                      </Link>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => deleteHandler(user._id)}
                        disabled={loadingDelete || user.isAdmin}
                        title={user.isAdmin ? 'Cannot delete admin' : 'Delete user'}
                      >
                        <FaTrash style={{ color: 'white' }} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default UserListScreen;
